import {
  PdfTemplate,
  PdfTemplateElement,
  PdfTemplateElementType,
  PdfTemplateLabel,
  PdfTemplatePage,
} from '../entities/form-pdf-template.entity';
import { Form, FormField } from '../entities/form.entity';
import {
  PDF_TABLE_CAPTION_HEIGHT_MM,
  PDF_TABLE_CONTINUATION_Y_MM,
  PDF_TABLE_SAFETY_MARGIN_MM,
  PdfBoundTableData,
  PdfBoundTablePaginationOptions,
  PdfBoundTableSliceMeta,
  PdfTablePageSlice,
  PdfTemplateElementWithTableSlice,
  PdfTemplateLabelWithTableSlice,
} from '../models/pdf-table-pagination.model';

const PT_TO_MM = 0.352778;
const LINE_HEIGHT_FACTOR = 1.2;
const CELL_PADDING_MM = 1.2;
const CELL_H_PADDING_MM = 1;
const CHAR_WIDTH_FACTOR = 0.45;

export function getPdfPageHeightMm(template: PdfTemplate): number {
  return template.orientation === 'landscape' ? 210 : 297;
}

export function getPdfPageWidthMm(template: PdfTemplate): number {
  return template.orientation === 'landscape' ? 297 : 210;
}

export function getPdfBodyHeightMm(
  page: PdfTemplatePage,
  template: PdfTemplate
): number {
  const pageHeight = getPdfPageHeightMm(template);
  const headerHeight = page.header?.height || 30;
  const footerHeight = page.footer?.height || 20;
  return Math.max(pageHeight - headerHeight - footerHeight, 8);
}

export function getBoundTableSliceMeta(
  element: PdfTemplateElement
): PdfBoundTableSliceMeta {
  const meta = element as PdfTemplateElementWithTableSlice;
  return {
    tableRowOffset: meta.tableRowOffset,
    tableRowLimit: meta.tableRowLimit,
    tableSliceRows: meta.tableSliceRows,
    tableShowTotals: meta.tableShowTotals,
    tableIsContinuation: meta.tableIsContinuation,
    tableContinuationLabel: meta.tableContinuationLabel,
    tableMinHeightMm: meta.tableMinHeightMm,
  };
}

export function isBoundTableLabel(
  element: PdfTemplateElement,
  form?: Form | null
): boolean {
  if (element.type !== PdfTemplateElementType.LABEL) {
    return false;
  }

  const label = element as PdfTemplateLabel;
  if (!label.bindingTagValue && !label.bindingTagUuid) {
    return false;
  }

  if (label.fieldType === 'table') {
    return true;
  }

  const field = findFormFieldByLabel(form, label);
  return field?.type === 'table';
}

export function findFormFieldByLabel(
  form: Form | null | undefined,
  label: PdfTemplateLabel
): FormField | undefined {
  if (!form?.fields) {
    return undefined;
  }

  const tagValue = label.bindingTagValue?.toUpperCase();
  const tagUuid = label.bindingTagUuid;

  return form.fields.find((field) =>
    field.metadata?.tags?.some(
      (tag) =>
        (tagValue && tag.value?.toUpperCase() === tagValue) ||
        (tagUuid && tag.uuid === tagUuid)
    )
  );
}

export function getTagDataItem(
  tagData: { [tag: string]: any } | undefined,
  label: PdfTemplateLabel
): unknown {
  if (!tagData) {
    return undefined;
  }

  const keys = [label.bindingTagValue, label.bindingTagUuid].filter(
    (key): key is string => !!key
  );

  for (const key of keys) {
    if (tagData[key] !== undefined) {
      return tagData[key];
    }
    const upper = key.toUpperCase();
    if (tagData[upper] !== undefined) {
      return tagData[upper];
    }
  }

  return undefined;
}

export function extractBoundTableData(
  tagDataItem: unknown,
  formField?: FormField
): PdfBoundTableData {
  let fields: any[] = [];
  let rows: any[] = [];

  if (Array.isArray(tagDataItem)) {
    rows = tagDataItem;
  } else if (tagDataItem && typeof tagDataItem === 'object') {
    const item = tagDataItem as Record<string, any>;
    fields =
      item['fields'] ||
      item['definition'] ||
      item['value']?.fields ||
      item['data']?.fields ||
      [];

    if (Array.isArray(item['rows'])) {
      rows = item['rows'];
    } else if (Array.isArray(item['data'])) {
      rows = item['data'];
    } else if (Array.isArray(item['data']?.rows)) {
      rows = item['data'].rows;
    } else if (Array.isArray(item['data']?.value?.rows)) {
      rows = item['data'].value.rows;
    } else if (Array.isArray(item['value'])) {
      rows = item['value'];
    } else if (Array.isArray(item['value']?.rows)) {
      rows = item['value'].rows;
    }
  }

  if (!fields.length && formField?.table?.fields) {
    fields = formField.table.fields;
  }

  const columnFields = (fields || []).filter(
    (field) =>
      field?.type === 'column' || (!field?.type && !field?.sub_fields?.length)
  );
  const displayFields = (columnFields.length > 0 ? columnFields : fields).map(
    (field: any) => ({
      ...field,
      uuid: String(field.uuid || '')
        .replace(/[-_]/g, '')
        .toUpperCase(),
    })
  );

  const normalizedRows = Array.isArray(rows)
    ? rows.map((row) => normalizeTableRow(row))
    : [];

  return {
    fields: displayFields,
    rows: normalizedRows,
    hasRowTotals: displayFields.some((field) => field.enable_row_totals),
    hasColumnTotals: displayFields.some((field) => field.enable_totals),
    columnWidthPercents: getBoundTableColumnWidthPercents(
      displayFields,
      displayFields.some((field) => field.enable_row_totals)
    ),
  };
}

export function applyBoundTableSlice(
  data: PdfBoundTableData,
  element: PdfTemplateElement
): PdfBoundTableData {
  const meta = getBoundTableSliceMeta(element);
  const slicedRows = Array.isArray(meta.tableSliceRows)
    ? meta.tableSliceRows
    : data.rows.slice(
        meta.tableRowOffset ?? 0,
        (meta.tableRowOffset ?? 0) + (meta.tableRowLimit ?? data.rows.length)
      );
  const showTotals = meta.tableShowTotals !== false && data.hasColumnTotals;

  return {
    ...data,
    rows: slicedRows,
    hasColumnTotals: showTotals,
    continuationLabel: meta.tableContinuationLabel,
    columnWidthPercents: getBoundTableColumnWidthPercents(
      data.fields,
      data.hasRowTotals
    ),
  };
}

export function paginateBoundTable(
  options: PdfBoundTablePaginationOptions
): PdfTablePageSlice[] {
  const sourceRows = options.rows || [];
  const fields = options.fields || [];
  const safetyMargin = options.safetyMarginMm ?? PDF_TABLE_SAFETY_MARGIN_MM;
  const hasRowTotals = !!options.hasRowTotals;

  if (sourceRows.length === 0) {
    return [
      {
        index: 0,
        rows: [],
        offset: 0,
        limit: 0,
        isFirst: true,
        isContinuation: false,
        isLast: true,
        showTotals: false,
      },
    ];
  }

  const headerHeightMm = estimateHeaderHeightMm(options.fontSizePt);
  const totalsHeightMm = options.hasColumnTotals
    ? estimateHeaderHeightMm(options.fontSizePt)
    : 0;
  const minRowMm = estimateLineHeightMm(options.fontSizePt) + CELL_PADDING_MM;

  const queue = sourceRows.map((row) => cloneRow(row));
  const slices: { rows: any[] }[] = [];
  let isFirst = true;

  while (queue.length > 0) {
    const availableHeight = isFirst
      ? options.firstPageAvailableHeightMm
      : options.continuationPageAvailableHeightMm;
    const budget = Math.max(
      availableHeight -
        safetyMargin -
        headerHeightMm -
        PDF_TABLE_CAPTION_HEIGHT_MM,
      0
    );
    const packed: any[] = [];
    let usedHeight = 0;

    while (queue.length > 0) {
      const row = queue[0];
      const rowHeight = estimateRowHeightMm(
        row,
        fields,
        options.tableWidthMm,
        options.fontSizePt,
        hasRowTotals
      );
      const remainingBudget = budget - usedHeight;
      const extra = queue.length === 1 ? totalsHeightMm : 0;

      if (rowHeight + extra <= remainingBudget) {
        packed.push(row);
        queue.shift();
        usedHeight += rowHeight;
        continue;
      }

      if (packed.length > 0 && remainingBudget - extra < minRowMm) {
        break;
      }

      const { fragment, leftover } = splitRowToFit(
        row,
        fields,
        Math.max(remainingBudget - extra, minRowMm),
        options.tableWidthMm,
        options.fontSizePt,
        hasRowTotals
      );
      packed.push(fragment);
      usedHeight += estimateRowHeightMm(
        fragment,
        fields,
        options.tableWidthMm,
        options.fontSizePt,
        hasRowTotals
      );
      if (leftover) {
        queue[0] = leftover;
        break;
      }
      queue.shift();
    }

    if (packed.length === 0 && queue.length > 0) {
      const { fragment, leftover } = splitRowToFit(
        queue[0],
        fields,
        Math.max(budget, minRowMm),
        options.tableWidthMm,
        options.fontSizePt,
        hasRowTotals
      );
      packed.push(fragment);
      if (leftover) {
        queue[0] = leftover;
      } else {
        queue.shift();
      }
    }

    slices.push({ rows: packed });
    isFirst = false;
  }

  return slices.map((slice, index) => {
    const isLast = index === slices.length - 1;
    return {
      index,
      rows: slice.rows,
      offset: 0,
      limit: slice.rows.length,
      isFirst: index === 0,
      isContinuation: index > 0,
      isLast,
      showTotals: isLast && options.hasColumnTotals,
    };
  });
}

export function expandPdfPagesForBoundTables(
  template: PdfTemplate,
  tagData: { [tag: string]: any } = {},
  form?: Form | null
): PdfTemplatePage[] {
  if (!template?.pages?.length) {
    return [];
  }

  const sortedPages = [...template.pages].sort((a, b) => a.order - b.order);
  const expanded: PdfTemplatePage[] = [];

  sortedPages.forEach((page) => {
    const bodyElements = page.body?.elements || [];
    const tableLabels = bodyElements.filter((element) =>
      isBoundTableLabel(element, form)
    ) as PdfTemplateLabel[];

    if (tableLabels.length === 0) {
      expanded.push(clonePage(page));
      return;
    }

    const bodyHeightMm = getPdfBodyHeightMm(page, template);
    const pageWidthMm = getPdfPageWidthMm(template);
    const firstPage = clonePage(page);
    const continuationJobs: {
      label: PdfTemplateLabel;
      slices: PdfTablePageSlice[];
    }[] = [];

    tableLabels
      .slice()
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .forEach((label, tableIndex) => {
        const field = findFormFieldByLabel(form, label);
        const tableData = extractBoundTableData(
          getTagDataItem(tagData, label),
          field
        );
        const firstAvailable = getFirstPageAvailableHeightMm(
          label,
          bodyElements,
          bodyHeightMm
        );
        const continuationAvailable = Math.max(
          bodyHeightMm - PDF_TABLE_CONTINUATION_Y_MM,
          8
        );
        const tableWidthMm = label.width || Math.max(pageWidthMm - label.x, 20);
        const slices = paginateBoundTable({
          fields: tableData.fields,
          rows: tableData.rows,
          fontSizePt: label.fontSize || 12,
          tableWidthMm,
          firstPageAvailableHeightMm: firstAvailable,
          continuationPageAvailableHeightMm: continuationAvailable,
          hasColumnTotals: tableData.hasColumnTotals,
          hasRowTotals: tableData.hasRowTotals,
        });

        decorateTableSlices(slices, tableIndex + 1);

        const firstSlice = slices[0];
        applySliceMetaToPage(firstPage, label.uuid, firstSlice);

        if (slices.length > 1) {
          continuationJobs.push({
            label,
            slices: slices.slice(1),
          });
        }
      });

    expanded.push(firstPage);

    continuationJobs.forEach(({ label, slices }) => {
      slices.forEach((slice) => {
        expanded.push(createContinuationPage(firstPage, label, slice));
      });
    });
  });

  expanded.forEach((page, index) => {
    page.order = index;
  });

  return expanded;
}

function getFirstPageAvailableHeightMm(
  element: PdfTemplateElement,
  bodyElements: PdfTemplateElement[],
  bodyHeightMm: number
): number {
  const nextElement = [...bodyElements]
    .filter(
      (candidate) =>
        candidate.uuid !== element.uuid &&
        candidate.type !== PdfTemplateElementType.LINE &&
        candidate.type !== PdfTemplateElementType.SHAPE
    )
    .sort((a, b) => a.y - b.y)
    .find((candidate) => candidate.y > element.y);

  const ceiling = nextElement ? nextElement.y : bodyHeightMm;
  const available = ceiling - (element.y || 0);

  return Math.max(available, 8);
}

function applySliceMetaToPage(
  page: PdfTemplatePage,
  labelUuid: string,
  slice: PdfTablePageSlice
): void {
  const target = page.body?.elements?.find(
    (element) => element.uuid === labelUuid
  ) as PdfTemplateLabelWithTableSlice | undefined;

  if (!target) {
    return;
  }

  target.tableRowOffset = slice.offset;
  target.tableRowLimit = slice.limit;
  target.tableSliceRows = slice.rows;
  target.tableShowTotals = slice.showTotals;
  target.tableIsContinuation = slice.isContinuation;
  target.tableContinuationLabel = slice.continuationLabel;
  target.height = undefined;
}

function createContinuationPage(
  sourcePage: PdfTemplatePage,
  label: PdfTemplateLabel,
  slice: PdfTablePageSlice
): PdfTemplatePage {
  const cloned = clonePage(sourcePage);
  const continuationLabel = cloned.body?.elements?.find(
    (element) => element.uuid === label.uuid
  ) as PdfTemplateLabelWithTableSlice | undefined;

  if (continuationLabel) {
    continuationLabel.uuid = `${label.uuid}__cont__${slice.index}`;
    continuationLabel.y = PDF_TABLE_CONTINUATION_Y_MM;
    continuationLabel.height = undefined;
    continuationLabel.tableRowOffset = slice.offset;
    continuationLabel.tableRowLimit = slice.limit;
    continuationLabel.tableSliceRows = slice.rows;
    continuationLabel.tableShowTotals = slice.showTotals;
    continuationLabel.tableIsContinuation = true;
    continuationLabel.tableContinuationLabel = slice.continuationLabel;
  }

  cloned.body = {
    ...cloned.body,
    elements: (cloned.body?.elements || []).filter((element) => {
      if (element.uuid === continuationLabel?.uuid) {
        return true;
      }
      return (
        element.type === PdfTemplateElementType.SHAPE ||
        element.type === PdfTemplateElementType.LINE
      );
    }),
  };

  cloned.uuid = `${sourcePage.uuid}__${label.uuid}__${slice.index}`;
  return cloned;
}

function clonePage(page: PdfTemplatePage): PdfTemplatePage {
  return JSON.parse(JSON.stringify(page));
}

function cloneRow(row: any): any {
  if (!row || typeof row !== 'object') {
    return row;
  }
  return { ...row };
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

function decorateTableSlices(
  slices: PdfTablePageSlice[],
  tableIndex: number
): void {
  if (slices.length < 2) {
    return;
  }

  slices.forEach((slice, index) => {
    slice.continuationLabel = `Table ${tableIndex}-${index + 1}`;
  });
}

function splitRowToFit(
  row: any,
  fields: any[],
  budgetMm: number,
  tableWidthMm: number,
  fontSizePt: number,
  hasRowTotals: boolean
): { fragment: any; leftover: any | null } {
  const lineHeight = estimateLineHeightMm(fontSizePt);
  let maxLines = Math.max(1, Math.floor(budgetMm / lineHeight));
  let result = splitRowAtLines(
    row,
    fields,
    maxLines,
    tableWidthMm,
    fontSizePt,
    hasRowTotals
  );

  while (result.leftover && maxLines < 400) {
    const fragmentHeight = estimateRowHeightMm(
      result.fragment,
      fields,
      tableWidthMm,
      fontSizePt,
      hasRowTotals
    );
    if (fragmentHeight + lineHeight > budgetMm) {
      break;
    }
    maxLines += 1;
    const next = splitRowAtLines(
      row,
      fields,
      maxLines,
      tableWidthMm,
      fontSizePt,
      hasRowTotals
    );
    const nextHeight = estimateRowHeightMm(
      next.fragment,
      fields,
      tableWidthMm,
      fontSizePt,
      hasRowTotals
    );
    if (nextHeight > budgetMm) {
      break;
    }
    result = next;
  }

  return result;
}

function splitRowAtLines(
  row: any,
  fields: any[],
  maxLines: number,
  tableWidthMm: number,
  fontSizePt: number,
  hasRowTotals: boolean
): { fragment: any; leftover: any | null } {
  const fragment = cloneRow(row);
  const leftover = cloneRow(row);
  let hasLeftover = false;

  fields.forEach((field) => {
    const chars = charsPerColumn(
      tableWidthMm,
      fields,
      fontSizePt,
      field,
      hasRowTotals
    );
    const full = cellText(row?.[field.uuid]);
    const { taken, rest } = takeTextForMaxLines(full, maxLines, chars);
    fragment[field.uuid] = taken;
    leftover[field.uuid] = rest;
    if (rest) {
      hasLeftover = true;
    }
  });

  if (!hasLeftover) {
    return { fragment: row, leftover: null };
  }

  return { fragment, leftover };
}

function takeTextForMaxLines(
  text: string,
  maxLines: number,
  charsPerLine: number
): { taken: string; rest: string } {
  if (!text) {
    return { taken: '', rest: '' };
  }

  const lines = wrapTextToLines(text, charsPerLine);
  if (lines.length <= maxLines) {
    return { taken: text, rest: '' };
  }

  const cut = lines[maxLines - 1].end;
  return {
    taken: text.slice(0, cut),
    rest: text.slice(cut),
  };
}

export function wrapTextToLines(
  text: string,
  charsPerLine: number
): { start: number; end: number }[] {
  if (!text) {
    return [{ start: 0, end: 0 }];
  }

  const width = Math.max(charsPerLine, 16);
  const lines: { start: number; end: number }[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    let next = consumeNextVisualLine(text, cursor, width);
    if (next <= cursor) {
      next = cursor + 1;
    }
    lines.push({ start: cursor, end: next });
    cursor = next;
  }

  return lines.length ? lines : [{ start: 0, end: 0 }];
}

function consumeNextVisualLine(
  text: string,
  start: number,
  charsPerLine: number
): number {
  const length = text.length;
  if (start >= length) {
    return length;
  }

  if (text[start] === '\n') {
    return start + 1;
  }

  const newline = text.indexOf('\n', start);
  const paragraphEnd = newline === -1 ? length : newline;
  const paragraphLength = paragraphEnd - start;

  if (paragraphLength <= charsPerLine) {
    return newline === -1 ? length : newline + 1;
  }

  const sliceEnd = Math.min(start + charsPerLine, paragraphEnd);
  const slice = text.slice(start, sliceEnd);
  const lastBreak = Math.max(slice.lastIndexOf(' '), slice.lastIndexOf('\n'));
  if (lastBreak >= charsPerLine * 0.85) {
    return start + lastBreak + 1;
  }

  return sliceEnd;
}

function normalizeTableRow(row: any): any {
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    return row;
  }

  const normalized: Record<string, any> = {};
  Object.keys(row).forEach((key) => {
    const normalizedKey = key.replace(/[-_]/g, '').toUpperCase();
    normalized[normalizedKey] = row[key];
  });
  return normalized;
}

function estimateLineHeightMm(fontSizePt: number): number {
  return fontSizePt * PT_TO_MM * LINE_HEIGHT_FACTOR;
}

function estimateHeaderHeightMm(fontSizePt: number): number {
  return estimateLineHeightMm(fontSizePt + 1) + CELL_PADDING_MM;
}

function charsPerLineFromWidthMm(
  colWidthMm: number,
  fontSizePt: number
): number {
  const usableWidthMm = Math.max(colWidthMm - CELL_H_PADDING_MM * 2 - 0.5, 8);
  const charWidthMm = fontSizePt * PT_TO_MM * CHAR_WIDTH_FACTOR;
  return Math.max(16, Math.floor(usableWidthMm / charWidthMm));
}

function columnSizeWeight(field: any): number {
  const raw =
    field?.size ?? field?.width ?? field?.columnWidth ?? field?.colWidth;
  if (raw === undefined || raw === null || raw === '') {
    return 100;
  }

  const numeric = Number.parseFloat(String(raw).trim().replace('%', ''));
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 100;
  }

  return numeric;
}

export function getBoundTableColumnWidthPercents(
  fields: any[],
  hasRowTotals: boolean
): number[] {
  const weights = (fields || []).map((field) => columnSizeWeight(field));
  if (hasRowTotals) {
    weights.push(100);
  }
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  return weights.map((weight) => (weight / total) * 100);
}

function charsPerColumn(
  tableWidthMm: number,
  fields: any[],
  fontSizePt: number,
  field: any,
  hasRowTotals: boolean
): number {
  const percents = getBoundTableColumnWidthPercents(fields, hasRowTotals);
  const fieldIndex = Math.max(
    0,
    fields.findIndex(
      (candidate) => candidate === field || candidate?.uuid === field?.uuid
    )
  );
  const percent = percents[fieldIndex] ?? percents[0] ?? 100;
  const colWidthMm = (tableWidthMm * percent) / 100;
  return charsPerLineFromWidthMm(colWidthMm, fontSizePt);
}

function estimateRowHeightMm(
  row: any,
  fields: any[],
  tableWidthMm: number,
  fontSizePt: number,
  hasRowTotals: boolean
): number {
  let maxLines = 1;

  fields.forEach((field) => {
    const chars = charsPerColumn(
      tableWidthMm,
      fields,
      fontSizePt,
      field,
      hasRowTotals
    );
    const text = cellText(row?.[field.uuid]);
    maxLines = Math.max(maxLines, wrapTextToLines(text, chars).length);
  });

  return maxLines * estimateLineHeightMm(fontSizePt) + CELL_PADDING_MM;
}
